import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { Zone } from '../../shared/types/zone'
import {
  clearMockUserCoordsStorage,
  getMockUserCoordsStorage,
  loadMockUserCoords,
  setMockUserCoordsStorage,
} from '../../shared/utils/loadMockUserCoords.utils'
import { checkZones } from './zoneDetectorThunks'
import { isLikelyMobile, isNonEmptyString } from '../../shared/utils'
import { hydrateZoneFromUserDossier } from '../Zone/zoneThunks'
import { SCENE_ROUTE } from '../../shared/constants/global'

const useZonesDetector = () => {
  const dispatch = useAppDispatch()

  // url and path
  const locationPathname = window.location.pathname
  const pathname = window.location.pathname
  const pathParams = pathname.split('/').filter(Boolean)
  const urlContainsSceneAndId =
    pathParams?.[0] === SCENE_ROUTE && isNonEmptyString(pathParams?.[1])

  // =======================
  // Selectors
  // =======================
  const {
    profile,
    loading: userLoading,
    error: userError,
  } = useAppSelector((state) => state.user)
  const { data: dossierData } = useAppSelector((state) => state.dossier)
  const {
    data: detectedZonesData,
    loading: detectedZonesLoading,
    error: detectedZonesError,
    userCoords,
  } = useAppSelector((state) => state.detectedZones)
  const {
    data: zoneData,
    loading: zoneLoading,
    loadingHydrateZone,
    error: zoneError,
  } = useAppSelector((state) => state.zone)
  const activeZoneId = zoneData?.id

  // check Mock User Coords in: query params and local storage
  const location = useLocation()
  const queryParams = queryString.parse(location.search)
  const mockLSCoords = getMockUserCoordsStorage()

  // =======================
  // State
  // =======================
  const [hasRequested, setHasRequested] = useState(false)
  const [mockUserCoords, setMockUserCoords] = useState<any>(null)
  const [initializing, setInitializing] = useState(true)
  const [error, setError] = useState<string>()

  // =======================
  // Effects
  // =======================
  useEffect(() => {
    const fetchCoords = async () => {
      if (queryParams?.lat && queryParams?.lng) {
        clearMockUserCoordsStorage()
        const coords = {
          latitude: Number(queryParams.lat),
          longitude: Number(queryParams.lng),
        }
        setMockUserCoords(coords)
        // Set mock coords in local storage
        setMockUserCoordsStorage(coords)
      } else if (mockLSCoords?.latitude && mockLSCoords?.longitude) {
        const coords = {
          latitude: Number(mockLSCoords.latitude),
          longitude: Number(mockLSCoords.longitude),
        }
        setMockUserCoords(coords)
      } else if (process.env.REACT_APP_USE_MOCK_COORDS === 'true') {
        const data = await loadMockUserCoords()
        setMockUserCoords(data)
      }
    }
    fetchCoords()
  }, [])

  // get user geo coords
  /* Suggestion from chat:
    Use watchPosition fallback (optional)

    If first attempt fails:

    navigator.geolocation.watchPosition(
      success,
      error,
      options
    );
    and: use last known location
  */
  const getPosition = (retries = 2): Promise<GeolocationPosition> =>
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        (err) => {
          if (err.code === 2 && retries > 0) {
            console.warn('Retrying geolocation...', retries)
            setTimeout(() => {
              getPosition(retries - 1)
                .then(resolve)
                .catch(reject)
            }, 1000)
          } else {
            reject(err)
          }
        },
        {
          enableHighAccuracy: isLikelyMobile(),
          timeout: 10000,
          maximumAge: 60000,
        }
      )
    })

  // find out if user has activeZoneId and is in any zones
  useEffect(() => {
    const getZones = async () => {
      if (!hasRequested && navigator.geolocation && !activeZoneId) {
        // if (!hasRequested && navigator.geolocation) {
        setHasRequested(true)
        setInitializing(true)
        try {
          let body: any = null

          const coords = getMockUserCoordsStorage()

          if (coords?.latitude && coords?.longitude) {
            // use stored mock coords
            body = {
              lat: coords.latitude,
              long: coords.longitude,
            }
          } else if (process.env.REACT_APP_USE_MOCK_COORDS === 'true') {
            const { MOCK_USER_COORDS } = await import('../../mockUserCoords')
            body = {
              lat: MOCK_USER_COORDS.latitude,
              long: MOCK_USER_COORDS.longitude,
            }
          } else {
            // only attempt geolocation if no mocks
            const position = await getPosition()

            const { latitude, longitude } = position.coords

            body = {
              lat: latitude,
              long: longitude,
            }
          }

          // if user does not have an active zone, and user on root path '/' check zone
          if (
            !detectedZonesData ||
            (!activeZoneId && locationPathname === '/')
          ) {
            // User does not have an active zone, and no zone data. Likely first time here?
            console.log('Debug: Check zones')
            dispatch(checkZones(body))
          } else if (locationPathname === '/') {
            console.log(
              'user has active zone and on root. should we redirect to dashbaord?'
            )
          } else if (locationPathname !== '/') {
            console.log(
              'no active zone and not root path. redirect or what? does this need to even be adressed?',
              {
                dispatch,
                hasRequested,
                mockUserCoords,
                activeZoneId,
                detectedZonesData,
              }
            )
          } else {
            console.log('do not query for zones')
          }
        } catch (err: any) {
          console.error('Geolocation failed:', err)

          setError(
            'Unable to determine your location. Please refesh the page or enable location services.'
          )
        } finally {
          setInitializing(false)
        }
      }
    }
    getZones()
  }, [dispatch, hasRequested, mockUserCoords, activeZoneId, detectedZonesData])

  // User is likely trying to access a specific scene from external site
  useEffect(() => {
    const loadSceneFromExternalSite = async () => {
      if (
        !loadingHydrateZone &&
        detectedZonesData &&
        !activeZoneId &&
        urlContainsSceneAndId
      ) {
        if (!dossierData) {
          await dispatch(hydrateZoneFromUserDossier())
        }
      }
    }
    pathParams && loadSceneFromExternalSite()
  }, [
    activeZoneId,
    detectedZonesData,
    urlContainsSceneAndId,
    loadingHydrateZone,
  ])

  // =======================
  // Memos
  // =======================

  // is the user inside their active zone?
  const activeZone = useMemo(() => {
    const insideActiveZone = detectedZonesData?.find(
      (zone: Zone) => zone.id === activeZoneId
    )
    // once we know whether to show the zone or not, we can stop initializing
    setInitializing(false)
    if (activeZoneId && !insideActiveZone) {
      alert('You left the zone. Now what?')
    }
    return insideActiveZone || null
  }, [activeZoneId, detectedZonesData])

  const loadingMsg = useMemo(() => {
    let msg = 'Zone Detector: '
    if (initializing) {
      msg += 'initilializing'
    } else if (userLoading) {
      msg += 'userLoading'
    } else if (detectedZonesLoading) {
      msg += 'detectedZonesLoading'
    } else if (loadingHydrateZone) {
      msg += 'loadingHydrateZone'
    } else {
      msg = 'Zone Detector'
    }
    return msg
  }, [initializing, userLoading, detectedZonesLoading, loadingHydrateZone])

  return {
    detectedZones: {
      data: detectedZonesData,
      loading: detectedZonesLoading,
      error: detectedZonesError,
    },
    loading:
      initializing || userLoading || detectedZonesLoading || loadingHydrateZone,
    loadingMsg,
    error: error || userError || detectedZonesError,
    activeZone,
    userCoords: userCoords?.lat ? userCoords : null,
    handlers: {},
  }
}

export default useZonesDetector
